import PlaceholderPicture from '@assets/images/placeholder.png';
import { themeColor } from '@config/theme';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { useTruncate } from '@hooks/useTruncate';
import ApartmentIcon from '@mui/icons-material/Apartment';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import { Button } from '@mui/material';
import notebookDisabled from 'assets/icons/notebookDisabled.svg';
import { useTranslation } from 'react-i18next';
import { useUsersStore } from 'services/users';
import CardFarmerInfo from './components/CardFarmerInfo';
import CardHeader from './components/CardHeader';

interface MonitoringCardSpecialistProps {
	data: any;
	loading: boolean;
	onSelectSpecialists: () => void;
	onChat?: (monitoringId: string) => void;
	chatLoading?: boolean;
}

const MonitoringCardSpecialist = ({
	data,
	loading,
	onSelectSpecialists,
	onChat,
	chatLoading,
}: MonitoringCardSpecialistProps) => {
	const { navigateWithLanguage } = useLanguageNavigate();
	const { user } = useUsersStore();
	const { t } = useTranslation();
	const { truncate } = useTruncate();
	const userRole = user?.role?.toLowerCase();

	// const f100Url =
	// 	`https://docs.google.com/forms/d/e/1FAIpQLSdWQkM6U2rZDuA78hSdwH6sYsApAMXx0ATWgqq0KUdW4AHhEg/viewform?usp=pp_url&entry.1605271730=${data.id}` ||
	// 	'';
	const f100Url = null;

	const specialistProfilePicture = data?.specialist?.profile_image
		? `${data?.specialist?.profile_image}`
		: PlaceholderPicture;

	const handleChatClick = () => {
		if (onChat && data?.id) {
			onChat(data.id);
		} else if (data?.channel?.id) {
			// Fallback to direct navigation if channel is already available
			navigateWithLanguage(`/${userRole}/chats?channelId=${data.channel.id}`);
		}
	};

	return (
		<div className="border rounded-lg shadow-md w-full bg-white relative">
			<CardHeader
				price={data?.services?.price}
				phases={data?.services?.phases}
				payVia={data?.pay_via}
				payment_status={'invoice_sent'}
				title={data?.crop?.name}
				isCompleted={data?.isCompleted}
				monitoringType={t('standardMonitoring')}
				invoice_url={data?.invoice_url}
				image={data?.crop?.image_url}
				f100={f100Url}
				duration={data?.services?.duration}
			/>
			<div className="w-full h-[1px] bg-[#E9ECEF]" />

			<div className="flex justify-between items-center max-xxs:flex-col flex-row">
				{data?.farmer && (
					<CardFarmerInfo
						farmer={data.farmer}
						channel={data?.channel}
						monitoringId={data?.id}
						onChat={onChat}
						chatLoading={chatLoading}
					/>
				)}
				<div className="flex flex-col items-start gap-2 pr-4">
					<div className="flex items-center gap-1 text-sm font-normal">
						<ApartmentIcon className="text-[#7B809A]" />
						<span className="text-[#7B809A] font-bold text-sm">
							{data?.area} {t(`unitTypes.${data?.unit}`)}
						</span>
					</div>
					<Button
						disabled
						sx={{
							color: themeColor.teal,
							fontFamily: 'Roboto',
							fontSize: '16px',
							fontStyle: 'normal',
							fontWeight: 700,
							lineHeight: 'normal',
							padding: 0,
							textTransform: 'capitalize',
							textDecoration: 'underline',
							gap: '4px',
						}}
						variant="text"
					>
						<img src={notebookDisabled} alt="notebookDisabled" />
						{t('fieldbook')}
					</Button>
				</div>
			</div>
			<div className="mt-3 py-2 flex justify-between bg-disabled border-y border-light px-5">
				<Button
					variant="text"
					onClick={handleChatClick}
					disabled={chatLoading || (!onChat && !data?.channel?.id)}
					sx={{
						padding: '0',
						fontWeight: '700',
						fontSize: '14px',
						border: 'unset',
					}}
					loading={chatLoading}
				>
					<QuestionAnswerOutlinedIcon className="mr-[10px]" />
					<span className="underline">{t('chat')}</span>
				</Button>
				<div className="flex items-center gap-2">
					<CheckCircleOutlineOutlinedIcon className="text-primary" />
					<span className="text-primary font-bold text-sm">{t('accepted')}</span>
				</div>
			</div>
			<div className="p-4">
				<Button
					variant="contained"
					onClick={onSelectSpecialists}
					fullWidth
					sx={{
						backgroundColor: '#00AB89',
						color: 'white',
						mt: 1,
					}}
				>
					{t('requestsAndOffers.ongoingProjects')}
				</Button>
			</div>
		</div>
	);
};

export default MonitoringCardSpecialist;
